import {
  ActivityTypeEnum,
  queryActivityQueue,
  TUserActivity,
} from '@/apis/promotions';
import { queryDailyRewardsList } from '@/apis/rewards';
import ActiveDepositeBonusDetails from '@/components/biz/rewards/ActiveDepositeBonusDetails';
import CatchErrorComp from '@/components/ui/CatchError';
import WrappedButton from '@/components/ui/WrappedButton';
import { isServer } from '@/configs';
import queryKeysFactory from '@/reactQueryKeys/keysFactory';
import { userInfoState } from '@/state/userInfo';
import { formatMoneyByCurrency } from '@/utils';
import { Input, Paper, Stack, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useQuery } from '@tanstack/react-query';
import { useRequest } from 'ahooks';
import { useAtomValue } from 'jotai';
import { noop } from 'lodash-es';
import { useTranslation } from 'next-i18next';
import { useRouter } from 'next/router';
import React, { useEffect, useMemo } from 'react';
import GeneralBonus from '../../components/pages/casino/rewards/general-bonus';
import { Box } from '@mui/system';
import useCurrencyRate from '@/hooks/biz/useCurrencyRateTable';

const PromoCode = ({ onRedeemSucces = noop }) => {
  const [t] = useTranslation('rewards');
  const [value, setValue] = React.useState('');
  const { runAsync, loading } = useRequest(
    () => {
      return new Promise((res) => {
        setTimeout(() => res(value), 2000);
      });
    },
    {
      manual: true,
      onSuccess: (data) => {
        console.log('%c~LOG~', 'color: yellow; font-size: 16px;', data);
        onRedeemSucces(data);
      },
    },
  );
  return (
    <Stack direction={'row'} alignItems={'center'} gap={1}>
      <Typography>
        {t('promo_code', {
          defaultValue: 'Promo Code',
        })}
      </Typography>
      <Input
        value={value}
        onChange={(e) => setValue(e.target.value)}
        placeholder={t('Code')}
      />
      <WrappedButton size="small" onClick={runAsync} isLoading={loading}>
        {t('Redeem')}
      </WrappedButton>
    </Stack>
  );
};

const Wrapper = styled(Paper)(({ theme }) => ({
  background: 'rgba(203, 215, 255, 0.03);',
  padding: theme.spacing(3),
}));

const RewardsPage = () => {
  const [t] = useTranslation('rewards');
  const { isUserLoading, userMetaData } = useAtomValue(userInfoState);

  const { replace } = useRouter();

  const { data } = useQuery<any>(queryKeysFactory.rewards.dailyRewardsList(), {
    refetchOnMount: 'always',
  });

  const { data: activitiesData, refetch } = useQuery<TUserActivity[]>(
    queryKeysFactory.rewards.activityQueue(),
    {
      refetchOnMount: 'always',
      enabled: true,
    },
  );

  const rewards = data?.data?.rewards || [];

  const activities: TUserActivity[] = activitiesData || [];

  const bonusMetrics = useMemo(() => {
    try {
      const available = rewards.reduce((acc, reward) => {
        return acc + reward.availableAmount ?? 0;
      }, 0);
      const totalClaimed = rewards.reduce((acc, reward) => {
        return acc + reward.claimedAmount ?? 0;
      }, 0);
      const totalReward = rewards.reduce((acc, reward) => {
        return acc + reward.totalAmount ?? 0;
      }, 0);
      const welcomeBonusRewarded =
        rewards.find((r) => r.bonus === 'DEPOSITBONUS')?.totalAmount ?? 0;
      const rakebackRewarded =
        rewards.find((r) => r.bonus === 'RAKEBACK')?.totalAmount ?? 0;
      return {
        available,
        totalReward,
        totalClaimed,
        welcomeBonusRewarded: Number(welcomeBonusRewarded),
        rakebackRewarded: Number(rakebackRewarded),
      };
    } catch (e) {
      return {
        available: 0,
        totalReward: 0,
        totalClaimed: 0,
        welcomeBonusRewarded: 0,
        rakebackRewarded: 0,
      };
    }
  }, [rewards]);

  const rewardedList = [
    {
      title: t('total_rewarded', {
        defaultValue: 'Total Rewarded',
      }),
      amount: bonusMetrics.totalReward,
    },
    {
      title: t('welcome_bonus', {
        defaultValue: 'Welcome Bonus',
      }),
      amount: bonusMetrics.welcomeBonusRewarded,
    },
    {
      title: t('rakeback', {
        defaultValue: 'Rakeback',
      }),
      amount: bonusMetrics.rakebackRewarded,
    },
  ];

  if (!isUserLoading && !isServer && !userMetaData) {
    replace('/benefits');
    return null;
  }

  return (
    <Stack gap={2}>
      <Typography variant="h6" color={'secondary.light'}>
        {t('reward_dashboard', {
          defaultValue: 'Reward Dashboard',
        })}
      </Typography>
      <Stack
        component={Wrapper}
        direction={['column', 'row']}
        justifyContent={'space-between'}
      >
        {rewardedList.map((item, idx) => (
          <Stack
            direction={['row', 'column']}
            justifyContent={'space-between'}
            key={item.title}
          >
            <Typography color={'secondary.light'}>{item.title}</Typography>
            <Typography
              color={idx === 0 ? 'success.main' : undefined}
              fontWeight={700}
            >
              {formatMoneyByCurrency(item.amount, {
                withCurrencySymbol: true,
              })}
            </Typography>
          </Stack>
        ))}
      </Stack>
      <Wrapper
        sx={{
          display: 'flex',
          flexDirection: ['column', 'column', 'row'],
          justifyContent: 'space-between',
        }}
      >
        <Box
          display={'flex'}
          gap={1}
          justifyContent={['space-between', 'flex-start']}
        >
          <Typography color={'secondary.light'}>
            {t('available_rewards', {
              defaultValue: 'Available Rewards: ',
            })}
          </Typography>
          <Typography color={'success.main'} fontSize={16} fontWeight={800}>
            {formatMoneyByCurrency(bonusMetrics.available, {
              withCurrencySymbol: true,
            })}
          </Typography>
        </Box>
        <Box
          display={'flex'}
          gap={1}
          justifyContent={['space-between', 'flex-start']}
        >
          <Typography color={'secondary.light'}>
            {t('total_claimed', {
              defaultValue: 'Total Claimed:',
            })}
          </Typography>
          <span>
            {formatMoneyByCurrency(bonusMetrics.totalClaimed, {
              withCurrencySymbol: true,
            })}
          </span>
        </Box>
        {/* <PromoCode /> */}
      </Wrapper>

      <>
        <Stack
          direction={['column', 'row']}
          justifyContent={'space-between'}
          alignItems={['flex-start', 'center']}
        >
          <Typography variant="h6" color={'secondary.light'}>
            {t('general_bonus', {
              defaultValue: 'General Bonus',
            })}
          </Typography>
          <Typography fontSize={12} color={'secondary.light'}>
            {t('only_one_bonus', {
              defaultValue:
                'Only one bonus can be activated. You cannot activate multiple bonuses at once.',
            })}
          </Typography>
        </Stack>

        <CatchErrorComp
          component={
            <>
              <GeneralBonus activities={activities} rewards={rewards} />
            </>
          }
        ></CatchErrorComp>
      </>
    </Stack>
  );
};

export default RewardsPage;
